<template>
  <div class="solutions" id="solutions">
    <div class="container">
      <div class="solutions__top">
        <h2 class="solutions__title">Our Solutions</h2>
        <div class="solutions__actions">
          <div class="solutions__actions-buttons">
            <button class="solutions__actions-button" ref="prevButton">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.5 7.5L4 12M4 12L8.5 16.5M4 12H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button class="solutions__actions-button" ref="nextButton">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 7.5L20 12M20 12L15.5 16.5M20 12H4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="solutions__actions-pagination" ref="paginationContainer"></div>
        </div>
      </div>
    </div>
    <swiper
        class="swiper"
        :speed="500"
        :modules="[Navigation, Pagination]"
        :navigation="{ prevEl: prevButton, nextEl: nextButton }"
        :pagination="{
          el: paginationContainer,
          type: 'bullets',
          clickable: true
        }"
        :breakpoints="{
          200: {
            slidesPerView: 1,
          },
          900: {
            slidesPerView: 2,
          },
          1200: {
            slidesPerView: 4,
            spaceBetween: 100
          },
          1680: {
            slidesPerView: 4,
            spaceBetween: 35
          }
        }"
    >
      <swiper-slide
          v-for="(slide, idx) in slides"
          :key="idx"
      >
        <div class="swiper__slide">
          <img :src="slide.image" alt="">
          <div class="swiper__slide-texts" @click="toggleSlide(idx)" :class="{ active: openSlides[idx] }">
            <div class="swiper__slide-texts-inner">
              {{ slide.title }}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.66667 1V10.3333M5.66667 10.3333L10.3333 5.66667M5.66667 10.3333L1 5.66667" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="swiper__slide-text" :class="{ active: openSlides[idx] }">
              {{ slide.text }}
            </div>
          </div>
        </div>
      </swiper-slide>
      <swiper-slide></swiper-slide>
      <swiper-slide></swiper-slide>
    </swiper>
  </div>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue';
import {Navigation, Pagination} from "swiper/modules";
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css';
import {ref} from "vue";

const prevButton = ref(null)
const nextButton = ref(null)
const paginationContainer = ref(null);

const slides = [
  {
    image: '/solutionsImage1.png',
    title: "Intelligent Data Strategy",
    text: "We turn raw data into actionable knowledge. By extracting strategic insights from complex datasets, we help you navigate decisions with confidence and unlock measurable value across your business."
  },
  {
    image: '/solutionsImage2.png',
    title: "Cyber Resilience & Digital Safety",
    text: "Security is no longer just an IT concern — it's a business imperative. Our holistic approach empowers you to mitigate digital threats, comply with global standards, and scale without compromising trust."
  },
  {
    image: '/solutionsImage3.png',
    title: "Innovation Consulting",
    text: "We partner with you to reimagine what’s possible. From identifying untapped opportunities to delivering scalable tech solutions, we equip your organization to grow smarter, not just faster."
  },
  {
    image: '/solutionsImage4.png',
    title: "Cloud Architecture & Optimization",
    text: "Whether you're transitioning to the cloud or refining your current environment, our cloud architects guide you through adoption, modernization, and performance tuning to ensure scalability, efficiency, and ROI."
  },
  {
    image: '/solutionsImage5.png',
    title: "Bespoke Software Solutions",
    text: "We design and develop software that adapts to your business — not the other way around. Every line of code we write is crafted to enhance your operations, support innovation, and deliver long-term impact."
  },
  {
    image: '/solutionsImage6.png',
    title: "AI & Process Automation",
    text: "Reclaim time and eliminate bottlenecks with intelligent automation. Our AI-driven systems streamline workflows, reduce errors, and let your teams focus on what truly matters."
  },
  {
    image: '/solutionsImage7.png',
    title: "Sustainable Tech Integration",
    text: "We help future-proof your business by embedding sustainability into your digital infrastructure — from energy-efficient cloud operations to ethical data use — aligning innovation with environmental responsibility."
  }
]

const openSlides = ref(Array(slides.length).fill(false));

function toggleSlide(index) {
  openSlides.value = openSlides.value.map((state, idx) =>
    idx === index ? !state : false
  );
}
</script>

<style lang="scss" scoped>
.solutions {
  padding-block: 100px 200px;
  background-image: url(@images/plusBg.png);
  background-repeat: no-repeat;
  -webkit-background-size: cover;
  background-size: cover;
  background-position: center;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 50px;
  }

  &__title {
    color: #272343;
    font-size: 52px;
    font-weight: 900;
    text-transform: capitalize;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    &-buttons {
      display: flex;
      align-items: center;
      gap: 18px;
    }

    &-button {
      cursor: pointer;
      border-radius: 50%;
      width: 44px;
      aspect-ratio: 1;
      display: grid;
      place-items: center;
      background-color: #007580;
      transition: 0.2s;

      @include hover {
        background-color: #00565e;
      }

      & svg {
         & path {
           stroke: $white;
         }
      }
    }

    &-pagination {
      text-align: center;
    }
  }
}

.swiper {
  left: 250px;
  overflow: visible;

  &__slide {
    position: relative;
    width: fit-content;

    &-texts {
      border-radius: 0 0 10px 10px;
      background-color: rgba(0, 0, 0, 0.70);
      backdrop-filter: blur(4.099999904632568px);
      cursor: pointer;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      padding: 30px 20px;
      transition: 0.2s;

      &.active {
        bottom: unset;
        top: 275px;
      }

      &-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;

        color: $white;
        font-size: 16px;
        font-weight: 600;
        text-transform: capitalize;
      }
    }

    &-text {
      height: 0;
      font-size: 0;
      overflow: hidden;
      transition: all 0.3s ease;

      &.active {
        height: auto;
        margin-top: 15px;
        font-size: 16px;
        color: $white;
        line-height: 1.5;
      }
    }
  }
}

.swiper-button-disabled {
  background-color: #F0F2F3;

  @include hover {
    background-color: #F0F2F3;
  }

  & svg {
    & path {
      stroke: #272343;
    }
  }
}

.swiper-slide .swiper__slide:after {
  content: '';
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.30);
  z-index: 1;
  border-radius: 10px;
}
.swiper-slide-active .swiper__slide:after {
  z-index: -1;
}
.swiper-slide-next .swiper__slide:after {
  z-index: -1;
}
.swiper-slide-next + .swiper-slide .swiper__slide:after {
  z-index: -1;
}
:deep(.swiper-pagination-bullet) {
  background-color: #007580;
}

@media (max-width: 1680px) {
  .swiper {
    left: 200px;

    &__slide {
      & img {
        width: 350px;
      }
    }
  }

  .swiper-slide {
    &:last-child {
      display: none;
    }
  }
}

@media (max-width: 1400px) {
  .swiper {
    left: 150px;
  }
}

@media (max-width: 1200px) {
  .swiper {
    left: 50px;
  }

  .swiper-slide {
    &:nth-child(8) {
      display: none;
    }
  }
}

@media (max-width: 650px) {
  .solutions__top {
    flex-direction: column;
    align-items: flex-start;
    gap: 30px;
  }
}

@media (max-width: 500px) {
  .swiper {
    left: unset;

    &__slide {
      & img {
        width: 350px;
      }
    }
  }

  .swiper-slide {
    display: grid;
    place-items: center;
  }
}
</style>